import {
  Avatar, Box,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack, IconButton, Spacer,
  Tag,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/store";
import { Image } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { HamburgerIcon, RepeatIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import unipdLogo from "assets/img/logo/logo-UNIPD-white.png";
import { MdFlag } from "react-icons/md";
import { FaUsers, FaFile, FaGlobe, FaFileExport, FaDiceD6 } from "react-icons/fa6";
import { resetDocumentsState, resetSelectedDocumentId } from "store/reducers/documents.slice";
import { removeUserLogout } from "store/reducers/auth.slice";


/**
 * Navbar component that provides navigation and user interaction functionalities.
 * 
 * @returns {JSX.Element} The rendered Navbar component.
 * 
 * @remarks
 * This component uses various hooks from React, Redux, and Chakra UI to manage state and UI behavior.
 * 
 * @component
 * 
 * @hook
 * - `useNavigate` from `react-router-dom` for navigation.
 * - `useSelector` from `react-redux` to access user and document state.
 * - `useDispatch` from `react-redux` to dispatch actions.
 * - `useDisclosure` from `@chakra-ui/react` to manage the drawer state.
 * - `useTranslation` from `react-i18next` for internationalization.
 * 
 * @function
 * - `logout` - Handles user logout, dispatches actions to reset state, and shows an alert.
 * - `onClickHandlerChangeDocument` - Resets the selected document ID and navigates to the dashboard.
 * 
 * @returns {JSX.Element} The rendered Navbar component.
 **/

export default function Navbar() {

  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: any) => state.user.isAuthenticated);
  const selectedDocumentId = useSelector((state: RootState) => state.documents.selectedDocumentId);
  const selectedDocument = useSelector((state: RootState) => state.documents.documents.find((doc) => doc._id === selectedDocumentId));


  const { isOpen, onOpen, onClose } = useDisclosure();

  const logout = () => {
    alert("Logout effettuato con successo!");
    dispatch(removeUserLogout());
    dispatch(resetDocumentsState());
    dispatch(resetSelectedDocumentId());
  };

  const onClickHandlerChangeDocument = () => {
    dispatch(resetSelectedDocumentId());
    navigate("/dashboard")
    onClose();
  }

  const { t } = useTranslation();

  return (
    <Flex
      as="nav"
      align="center"
      justify="space-between"
      wrap="wrap"
      w="100%"
      p={2}
      bg={"brand.500"}
      color={"white"}
    >
      <Link to="dashboard">
        <HStack>
          <Box alignContent={"center"}>
            <Image w="75%" src={unipdLogo} alt="" />
          </Box>
        </HStack>
      </Link>

      <Spacer />
      {isAuthenticated && (
        <nav>
          <ButtonGroup gap="2">
            <Tag size={"xl"} fontWeight={"bold"} fontSize={"xl"} colorScheme={"gray"} paddingX={2}>
              {selectedDocument?.title} (codice: {selectedDocument?.code})
            </Tag>

            <IconButton
              variant="outline"
              colorScheme="grey"
              aria-label="Apri menu"
              icon={<HamburgerIcon />}
              onClick={onOpen}
            />
          </ButtonGroup>

          <Drawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
          >
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader></DrawerHeader>
              <DrawerBody>
                <Flex>
                  <Avatar />
                  <Box ml="3">
                    <Text fontWeight="bold">
                      {user?.name} {user?.surname}
                    </Text>
                    <Text fontSize="sm">{user?.email}</Text>
                  </Box>
                </Flex>
                <Divider my={5} />

                <Box>
                  <Text fontSize={"xs"} textTransform={"uppercase"} fontWeight={"bold"}>Documento selezionato:</Text>
                  <Tag colorScheme={"gray"} variant={"solid"} width={"100%"}>
                    {selectedDocument?.title} (codice: {selectedDocument?.code})
                  </Tag>
                  <Button onClick={onClickHandlerChangeDocument} colorScheme="blue" variant="outline" size="sm" mt={2} leftIcon={<RepeatIcon />} width={"100%"}>
                    Cambia documento
                  </Button>
                </Box>

                <Divider my={5} />
                <Box my={5} color={window.location.pathname.includes("/targets") ? "brand.500" : "black"}
                  textDecoration={window.location.pathname.includes("/targets") ? "underline" : "none"}
                >
                  <Link to="/targets" onClick={onClose}>
                    <Box display="flex" alignItems="center">
                      <Box as={MdFlag} mr={2} />
                      Obiettivi
                    </Box>
                  </Link>
                </Box>

                <Box my={5} color={window.location.pathname.includes("/overview") ? "brand.500" : "black"}
                  textDecoration={window.location.pathname.includes("/overview") ? "underline" : "none"}
                >
                  <Link to="/overview" onClick={onClose}>
                    <Box display="flex" alignItems="center">
                      <Box as={FaGlobe} mr={2} />
                      Panoramica
                    </Box>
                  </Link>
                </Box>

                <Box my={5} color={window.location.pathname.includes("/export") ? "brand.500" : "black"}
                  textDecoration={window.location.pathname.includes("/export") ? "underline" : "none"}
                >
                  <Link to="/export" onClick={onClose}>
                    <Box display="flex" alignItems="center">
                      <Box as={FaFileExport} mr={2} />
                      Esporta dati
                    </Box>
                  </Link>
                </Box>

                {
                  user.isAdmin && <>
                    <Divider my={5} />
                    <Box my={5} color={window.location.pathname.includes("/roles") ? "brand.500" : "black"}
                      textDecoration={window.location.pathname.includes("/roles") ? "underline" : "none"}
                    >

                      <Link to="/roles" onClick={onClose}>
                        <Box display="flex" alignItems="center">
                          <Box as={FaUsers} mr={2} />
                          Gestione Utenti e Ruoli
                        </Box>
                      </Link>
                    </Box>

                    <Box my={5} color={window.location.pathname.includes("/documents") ? "brand.500" : "black"}
                      textDecoration={window.location.pathname.includes("/documents") ? "underline" : "none"}
                    >
                      <Link to="/documents" onClick={onClose}>
                        <Box display="flex" alignItems="center">
                          <Box as={FaFile} mr={2} />
                          Gestione Documenti
                        </Box>
                      </Link>
                    </Box>

                    <Box my={5} color={window.location.pathname.includes("/sources") ? "brand.500" : "black"}
                      textDecoration={window.location.pathname.includes("/sources") ? "underline" : "none"}
                    >
                      <Link to="/sources" onClick={onClose}>
                        <Box display="flex" alignItems="center">
                          <Box as={FaDiceD6} mr={2} />
                          Gestione Fonti
                        </Box>
                      </Link>
                    </Box>

                  </>
                }
              </DrawerBody>
              <DrawerFooter>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant={"outline"}
                  onClick={logout}
                  width="full"
                >
                  {t("general.signOutButton.text")}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </nav>)}
    </Flex>
  );
}
